import { useState } from "react";
import { ColumnDef, SortDir } from "./types";

export interface UseSortResult<TSortKey extends string> {
  readonly sortKey: TSortKey;
  readonly sortDir: SortDir;
  readonly setSort: (key: TSortKey) => void;
  /**
   * Return the sort indicator (▲ / ▼ / ⇅) for a column, or null if the
   * column isn't sortable. Bound to this hook's state so callers don't
   * have to thread sortKey/sortDir manually.
   */
  readonly indicator: <TKey extends string>(
    col: ColumnDef<TKey, TSortKey>,
  ) => string | null;
}

/**
 * Sortable-column state with the toggle convention used across the app:
 * clicking the active column flips direction; clicking a different
 * column resets to ascending.
 */
export function useSort<TSortKey extends string>(
  initialKey: TSortKey,
  initialDir: SortDir = "asc",
): UseSortResult<TSortKey> {
  const [sortKey, setSortKey] = useState<TSortKey>(initialKey);
  const [sortDir, setSortDir] = useState<SortDir>(initialDir);

  const setSort = (key: TSortKey) => {
    if (key === sortKey) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const indicator = <TKey extends string>(
    col: ColumnDef<TKey, TSortKey>,
  ): string | null => {
    if (!col.sortKey) return null;
    if (col.sortKey === sortKey) return sortDir === "asc" ? " ▲" : " ▼";
    return " ⇅";
  };

  return { sortKey, sortDir, setSort, indicator };
}
